/**
 * Camera lifecycle — getUserMedia with clean teardown.
 * @file src/web/features/face/hooks/useCamera.ts
 *
 * @llm-rule WHEN: A screen needs the live camera preview
 * @llm-rule AVOID: Calling getUserMedia inline — the stream leaks if the component unmounts mid-request
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type CameraState = 'idle' | 'starting' | 'live' | 'denied' | 'unavailable';

export interface UseCameraResult {
  videoRef: React.RefObject<HTMLVideoElement>;
  state: CameraState;
  error: string | null;
  start: () => Promise<void>;
  stop: () => void;
}

export function useCamera(autoStart = true): UseCameraResult {
  const videoRef = useRef<HTMLVideoElement>(null!);
  const streamRef = useRef<MediaStream | null>(null);
  /** Guards against a stream arriving after unmount — otherwise it stays on. */
  const mountedRef = useRef(true);

  const [state, setState] = useState<CameraState>('idle');
  const [error, setError] = useState<string | null>(null);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setState('idle');
  }, []);

  const start = useCallback(async () => {
    if (streamRef.current) return;

    if (!navigator.mediaDevices?.getUserMedia) {
      setState('unavailable');
      setError('This browser cannot access a camera. Try Chrome, Edge, or Safari over HTTPS.');
      return;
    }

    setState('starting');
    setError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });

      // The component may have unmounted while the permission prompt was up.
      if (!mountedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {
          /* autoplay rejection is recoverable — the user can tap to retry */
        });
      }
      setState('live');
    } catch (err: any) {
      if (!mountedRef.current) return;

      if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
        setState('denied');
        setError('Camera access was blocked. Allow it in your browser settings, then try again.');
      } else if (err?.name === 'NotFoundError' || err?.name === 'OverconstrainedError') {
        setState('unavailable');
        setError('No camera was found on this device.');
      } else {
        setState('unavailable');
        setError(err?.message || 'The camera could not be started.');
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (autoStart) void start();

    return () => {
      mountedRef.current = false;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
    // `start` is stable (empty deps), so this runs once per mount.
  }, [autoStart, start]);

  return { videoRef, state, error, start, stop };
}
